import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { PatchTestSection, PATCH_TEST_CONFIG, QuestionsSections } from '~/components';
import withDynamicDestroyOnUnmount from '~/components/with-dynamic-form-destroy';
import { FORM_NAME } from '~/utils/reduxForm.constants';
import { YourDetails, YOUR_DETAILS_FIELDS as defaultYourDetailsFields } from './sections';

import styles from './AboutYouForm.scss';

// Custom validation function to replace jl-design-system validate
const validateField = (value, field) => {
  // If field has validate array with functions, use it
  if (field.validate && Array.isArray(field.validate)) {
    for (const validator of field.validate) {
      // Ensure validator is a function before calling it
      if (typeof validator === 'function') {
        const error = validator(value);
        if (error) return error;
      }
    }
    return undefined;
  }

  // Otherwise, validate based on field metadata (required, regex, maxLength, etc.)
  // Check required validation
  if (field.required) {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return field.validationMessageKeys?.required || `${field.label || field.name} is required`;
    }
  }

  // Check regex validation (only for string values)
  if (value && typeof value === 'string' && field.regex && !field.regex.test(value)) {
    return field.validationMessageKeys?.regex || `${field.label || field.name} is invalid`;
  }

  // Check maxLength validation (only for string values)
  if (value && typeof value === 'string' && field.maxLength && value.length > field.maxLength) {
    return field.validationMessageKeys?.maxLength || `${field.label || field.name} is too long`;
  }

  return undefined;
};

const validateFields = (values, fields) => {
  const errors = {};

  fields.forEach((field) => {
    const error = validateField(values?.[field.name], field);
    if (error) {
      errors[field.name] = error;
    }
  });

  return Object.keys(errors).length > 0 ? errors : undefined;
};

export class AboutYouForm extends PureComponent {
  componentDidMount() {
    // No longer need to dispatch blur for validation initialization
  }

  isNotEmptyQuestions = (questions) => Object.values(questions).some((arr) => arr.length > 0);

  canDisplayQuestionsSection = () => {
    const { questions, questionsSectionsConfig } = this.props;
    const displayQuestionsInYourDetails = questionsSectionsConfig[0]?.displayQuestionsInYourDetails;
    return this.isNotEmptyQuestions(questions) && !displayQuestionsInYourDetails;
  };

  validateForm = (values) => {
    const {
      yourDetailsSection: yourDetailsSectionAnswers,
      patchTestSection: patchTestAnswer = {},
      questionsSection: questionsSectionAnswers = {},
    } = values;

    const {
      yourDetailsFields = defaultYourDetailsFields,
      questions,
      displayPatchTestSection,
      questionsSectionsConfig,
    } = this.props;

    const errors = {};

    const displayQuestionsInYourDetails = questionsSectionsConfig[0]?.displayQuestionsInYourDetails;

    // Validate your details section
    const yourDetailsErrors = validateFields(yourDetailsSectionAnswers, yourDetailsFields);
    if (yourDetailsErrors) {
      errors.yourDetailsSection = yourDetailsErrors;
    }

    // Validate patch test section only if it's displayed
    if (displayPatchTestSection) {
      const patchTestErrors = validateFields(patchTestAnswer, PATCH_TEST_CONFIG);
      if (patchTestErrors) {
        errors.patchTestSection = patchTestErrors;
      }
    }

    // Validate questions section
    if (questions.RETRIEVED_QUESTIONS) {
      const answersSource = displayQuestionsInYourDetails
        ? yourDetailsSectionAnswers
        : questionsSectionAnswers;
      const questionErrors = this.validateQuestionsSections(
        questions.RETRIEVED_QUESTIONS,
        answersSource,
      );
      if (Object.keys(questionErrors).length > 0) {
        const sectionKey = displayQuestionsInYourDetails
          ? 'yourDetailsSection'
          : 'questionsSection';
        errors[sectionKey] = { ...(errors[sectionKey] || {}), ...questionErrors };
      }
    }

    // Return empty object if no errors, otherwise return errors object
    return Object.keys(errors).length > 0 ? errors : {};
  };

  validateQuestionsSections = (questions, answers) =>
    questions.reduce((acc, question) => {
      const questionAnswer = answers?.[question.reduxFieldConfig.name];
      const validators = question.reduxFieldConfig.validate;

      // Ensure validators exist and is an array before filtering
      if (!validators || !Array.isArray(validators)) {
        return { ...acc };
      }

      const validationResult = validators
        .filter((validation) => typeof validation === 'function')
        .map((validation) => validation(questionAnswer))
        .filter((value) => !!value);
      return validationResult[0]
        ? { ...acc, [question.reduxFieldConfig.name]: validationResult[0] }
        : { ...acc };
    }, {});

  render() {
    const {
      children,
      onSubmit,
      formId,
      timeSlotComponent,
      displayPatchTestSection,
      questions,
      sectionDataTest,
      questionsSectionsConfig,
      initialValues,
    } = this.props;

    return (
      <Form
        onSubmit={(values, form) => {
          return onSubmit(values, form);
        }}
        initialValues={initialValues}
        validate={(values) => this.validateForm(values)}
        keepDirtyOnReinitialize
        mutators={{
          setFieldTouched: ([fieldName, touched], state) => {
            const field = state.fields[fieldName];
            if (field) {
              field.touched = touched;
            }
          },
        }}
        render={({ handleSubmit, form }) => {
          return (
            <>
              <form
                id={formId}
                onSubmit={(e) => {
                  e.preventDefault();

                  // Get current form state
                  const formState = form.getState();

                  // If form has errors, mark all fields as touched to show validation errors
                  if (formState.errors && Object.keys(formState.errors).length > 0) {
                    // Mark all fields in all sections as touched
                    Object.keys(formState.errors).forEach((sectionKey) => {
                      const sectionErrors = formState.errors[sectionKey];
                      if (typeof sectionErrors === 'object') {
                        Object.keys(sectionErrors).forEach((fieldKey) => {
                          const fullFieldName = `${sectionKey}.${fieldKey}`;
                          form.mutators.setFieldTouched(fullFieldName, true);
                        });
                      }
                    });

                    // Scroll to first error after a brief delay to let the UI update
                    setTimeout(() => {
                      const firstError = document.querySelector('[class*="error"]');
                      if (firstError) {
                        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }

                  handleSubmit(e);
                }}
                className={styles.AboutYouForm}
                noValidate
                data-testid="AboutYouForm"
              >
                {displayPatchTestSection ? (
                  <Field name="patchTestSection.patchTest">
                    {({ input, meta }) => (
                      <PatchTestSection
                        onFieldChange={(fieldName, value) => input.onChange(value)}
                        initialValue={input.value}
                        error={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                ) : null}
                {timeSlotComponent}
                <div className={styles.formWrapper}>
                  <YourDetails formName={FORM_NAME} fieldNamePrefix="yourDetailsSection." />
                  {this.canDisplayQuestionsSection() ? (
                    <QuestionsSections
                      formSectionName="questionsSection"
                      questions={questions}
                      dataTest={sectionDataTest}
                      questionsSectionsConfig={questionsSectionsConfig}
                    />
                  ) : null}
                </div>
                <Field name="initValidationInput" type="hidden" component="input" />
                {children}
              </form>
            </>
          );
        }}
      />
    );
  }
}

const mapStateToProps = (state) => {
  const {
    appointment: {
      draft: { selectedServiceTypeChanged },
    },
  } = state;

  return { selectedServiceTypeChanged };
};

const mapDispatchToProps = {};

const WrappedAboutYouForm = withDynamicDestroyOnUnmount(AboutYouForm);

export const ConnectedAboutYouReduxForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(WrappedAboutYouForm);

AboutYouForm.propTypes = {
  children: PropTypes.node,
  timeSlotComponent: PropTypes.node,
  onSubmit: PropTypes.func.isRequired,
  questions: PropTypes.shape({}).isRequired,
  questionsSectionsConfig: PropTypes.arrayOf(PropTypes.shape({})),
  sectionDataTest: PropTypes.string,
  displayPatchTestSection: PropTypes.bool.isRequired,
  dispatchSelectedServiceTypeChanged: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  yourDetailsFields: PropTypes.array,
};

AboutYouForm.defaultProps = {
  sectionDataTest: null,
  questionsSectionsConfig: [],
  timeSlotComponent: null,
};
